// src/components/RecipeVisualEditor.js

import React, { useState, useEffect } from 'react';
import { 
    ArrowLeft, Sparkles, Clock, Users, Youtube, 
    Plus, Trash2, ChevronUp, ChevronDown, Camera, 
    Check, X, ClipboardList, BookOpen, ChefHat, MessageSquare
} from 'lucide-react';
import { callApi } from '../api';
import ImageUploader from './ImageUploader';
import AutoTextarea from './AutoTextarea';

const RecipeVisualEditor = ({ tarifToEdit, kulup, onSave, onCancel }) => {
    const initialState = {
        title: '',
        category: 'Yurt Tarifleri',
        description: '',
        instructions: [''],
        author: kulup?.ad || 'KluGastro Şefi',
        imageBase64: '',
        time: '45',
        servings: '4 Kişilik',
        rating: '4.8',
        videoUrl: ''
    };

    const [formData, setFormData] = useState(initialState);

    // Multi-Section Ingredients State with individual item rows
    // Structure: [{ id: '...', title: 'Krema İçin', items: ['500 ml süt', '1 çubuk tarçın'] }]
    const [ingredientSections, setIngredientSections] = useState([
        { id: 'sec-init', title: '', items: [''] }
    ]);

    // Bulk Paste Modal State
    const [bulkSectionId, setBulkSectionId] = useState(null);
    const [bulkText, setBulkText] = useState('');

    // Media Uploader Toggle
    const [showUploader, setShowUploader] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Tabs: 'editor' | 'comments'
    const [activeTab, setActiveTab] = useState('editor');
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);

    // AI Modal State
    const [showAiModal, setShowAiModal] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (tarifToEdit) {
            // Parse instructions
            let parsedInstructions = [''];
            let parsedVideoUrl = tarifToEdit.videoUrl || '';
            const rawInstructions = tarifToEdit.instructions || tarifToEdit.yapilis;

            if (rawInstructions) {
                if (Array.isArray(rawInstructions)) {
                    parsedInstructions = rawInstructions;
                } else if (typeof rawInstructions === 'string') {
                    try {
                        const parsed = JSON.parse(rawInstructions);
                        if (Array.isArray(parsed)) parsedInstructions = parsed;
                        else if (parsed && parsed.adimlar) {
                            parsedInstructions = parsed.adimlar;
                            if (parsed.videoUrl) parsedVideoUrl = parsed.videoUrl;
                        } else parsedInstructions = rawInstructions.split('\n');
                    } catch (e) {
                        parsedInstructions = rawInstructions.split('\n');
                    }
                }
            }

            // Parse Ingredients into Sections with individual rows
            const rawIngredients = tarifToEdit.ingredients || tarifToEdit.malzemeler;
            let loadedSections = [];

            if (Array.isArray(rawIngredients)) {
                let currentSection = { id: 'sec-' + Math.random().toString(36).substr(2, 9), title: '', items: [] };

                rawIngredients.forEach(item => {
                    if (typeof item === 'object' && item.type === 'HEADER') {
                        if (currentSection.items.length > 0 || currentSection.title !== '') {
                            loadedSections.push(currentSection);
                        }
                        currentSection = { 
                            id: 'sec-' + Math.random().toString(36).substr(2, 9), 
                            title: item.title || '', 
                            items: [] 
                        };
                    } else if (typeof item === 'string') {
                        // Could be comma separated if saved from previous legacy version
                        if (item.includes(',')) {
                            const subItems = item.split(',').map(s => s.trim()).filter(Boolean);
                            currentSection.items.push(...subItems);
                        } else if (item.trim()) {
                            currentSection.items.push(item.trim());
                        }
                    } else if (typeof item === 'object' && item.name) {
                        currentSection.items.push(item.name.trim());
                    }
                });

                if (currentSection.items.length > 0 || currentSection.title !== '') {
                    loadedSections.push(currentSection);
                }
            } else if (typeof rawIngredients === 'string') {
                const lines = rawIngredients.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
                loadedSections = [{ 
                    id: 'sec-' + Math.random().toString(36).substr(2, 9), 
                    title: '', 
                    items: lines.length > 0 ? lines : [''] 
                }];
            }

            if (loadedSections.length === 0) {
                loadedSections = [{ id: 'sec-default', title: '', items: [''] }];
            }

            // Ensure each section has at least one item row
            loadedSections = loadedSections.map(sec => ({
                ...sec,
                items: sec.items.length > 0 ? sec.items : ['']
            }));

            setIngredientSections(loadedSections);

            setFormData({
                title: tarifToEdit.title || tarifToEdit.ad || '',
                category: tarifToEdit.category || tarifToEdit.kategori || 'Yurt Tarifleri',
                description: tarifToEdit.description || tarifToEdit.aciklama || '',
                instructions: parsedInstructions.length > 0 ? parsedInstructions : [''],
                author: tarifToEdit.author || '',
                imageBase64: tarifToEdit.imageBase64 || tarifToEdit.resimUrl || '',
                time: tarifToEdit.time || tarifToEdit.hazirlamaSuresi || '45',
                servings: tarifToEdit.servings || tarifToEdit.kacKisilik || '4 Kişilik',
                rating: tarifToEdit.rating || '4.8',
                videoUrl: parsedVideoUrl
            });
        }
    }, [tarifToEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // --- Ingredient Section & Item Row Logic ---
    const handleSectionTitleChange = (secId, title) => {
        setIngredientSections(prev => prev.map(sec => 
            sec.id === secId ? { ...sec, title } : sec
        ));
    };

    const handleItemChange = (secId, itemIdx, value) => {
        setIngredientSections(prev => prev.map(sec => {
            if (sec.id !== secId) return sec;
            const updatedItems = [...sec.items];
            updatedItems[itemIdx] = value;
            return { ...sec, items: updatedItems };
        }));
    };

    const addItemRow = (secId) => {
        setIngredientSections(prev => prev.map(sec => {
            if (sec.id !== secId) return sec;
            return { ...sec, items: [...sec.items, ''] };
        }));
    };

    const removeItemRow = (secId, itemIdx) => {
        setIngredientSections(prev => prev.map(sec => {
            if (sec.id !== secId) return sec;
            const filtered = sec.items.filter((_, idx) => idx !== itemIdx);
            return { ...sec, items: filtered.length > 0 ? filtered : [''] };
        }));
    };

    const addSection = () => {
        setIngredientSections(prev => [
            ...prev,
            { id: 'sec-' + Math.random().toString(36).substr(2, 9), title: '', items: [''] }
        ]);
    };

    const removeSection = (secId) => {
        if (ingredientSections.length > 1) {
            setIngredientSections(prev => prev.filter(sec => sec.id !== secId));
        } else {
            setIngredientSections([{ id: 'sec-reset', title: '', items: [''] }]);
        }
    };

    // Bulk Paste Handler: splits lines or commas into rows
    const handleApplyBulkText = () => {
        if (!bulkText.trim() || !bulkSectionId) {
            setBulkSectionId(null);
            return;
        }

        const parsed = bulkText
            .split(/[\n,]/)
            .map(line => line.trim())
            .filter(Boolean);

        if (parsed.length > 0) {
            setIngredientSections(prev => prev.map(sec => {
                if (sec.id !== bulkSectionId) return sec;
                const existing = sec.items.filter(Boolean);
                return { ...sec, items: [...existing, ...parsed] };
            }));
        }

        setBulkText('');
        setBulkSectionId(null);
    };

    // --- Steps Management Logic ---
    const handleStepChange = (index, value) => {
        const newSteps = [...formData.instructions];
        newSteps[index] = value;
        setFormData(prev => ({ ...prev, instructions: newSteps }));
    };

    const addStep = () => {
        setFormData(prev => ({ ...prev, instructions: [...prev.instructions, ''] }));
    };

    const removeStep = (index) => {
        const newSteps = formData.instructions.filter((_, i) => i !== index);
        setFormData(prev => ({ 
            ...prev, 
            instructions: newSteps.length > 0 ? newSteps : [''] 
        }));
    };

    const moveStep = (index, direction) => {
        const newSteps = [...formData.instructions];
        if (direction === 'up' && index > 0) {
            [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
        } else if (direction === 'down' && index < newSteps.length - 1) {
            [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
        }
        setFormData(prev => ({ ...prev, instructions: newSteps }));
    };

    // --- Save & Submit Logic ---
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        setError(null);

        const validSteps = formData.instructions.filter(step => step && step.trim().length > 0);

        // Flatten ingredients preserving section headers
        let finalIngredients = [];
        ingredientSections.forEach(sec => {
            const validItems = sec.items.map(i => i.trim()).filter(Boolean);
            if (validItems.length > 0) {
                if (sec.title && sec.title.trim() !== '') {
                    finalIngredients.push({ type: 'HEADER', title: sec.title.trim() });
                }
                finalIngredients.push(...validItems);
            }
        });

        try {
            const kulupQuery = kulup?.kulupID ? `?kulupID=${kulup.kulupID}` : '';
            const yapilisObject = {
                adimlar: validSteps,
                videoUrl: formData.videoUrl
            };

            const finalData = {
                kulupID: kulup?.kulupID,
                id: tarifToEdit?.recipeId || tarifToEdit?.id,
                recipeId: tarifToEdit?.recipeId || tarifToEdit?.id,
                ad: formData.title,
                aciklama: formData.description,
                malzemeler: finalIngredients,
                yapilis: JSON.stringify(yapilisObject),
                kategori: formData.category,
                hazirlamaSuresi: formData.time,
                pisirmeSuresi: '15',
                kacKisilik: formData.servings,
                rating: formData.rating,
                resimUrl: formData.imageBase64,
            };

            if (tarifToEdit) {
                await callApi(`/recipes${kulupQuery}`, 'PUT', finalData);
            } else {
                await callApi(`/recipes${kulupQuery}`, 'POST', finalData);
            }
            onSave();
        } catch (err) {
            setError(err.message || 'Tarif kaydedilirken bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    // Fetch comments if comments tab is active
    useEffect(() => {
        if (activeTab === 'comments' && (tarifToEdit?.recipeId || tarifToEdit?.id)) {
            const fetchComments = async () => {
                setCommentsLoading(true);
                try {
                    const id = tarifToEdit.recipeId || tarifToEdit.id;
                    const data = await callApi(`/recipes/${id}/comments`, 'GET');
                    if (data && data.comments) {
                        setComments(data.comments);
                    }
                } catch (e) {
                    console.error("Yorumlar alınamadı", e);
                } finally {
                    setCommentsLoading(false);
                }
            };
            fetchComments();
        }
    }, [activeTab, tarifToEdit]);

    const handleDeleteComment = async (commentId) => {
        if (window.confirm("Bu yorumu kalıcı olarak silmek istediğinize emin misiniz?")) {
            try {
                await callApi(`/recipes/comments/${commentId}`, 'DELETE');
                setComments(prev => prev.filter(c => c.yorumId !== commentId));
            } catch (e) {
                alert("Yorum silinirken bir hata oluştu: " + e.message);
            }
        }
    };

    // AI Recipe Generator
    const handleAiGenerate = async () => {
        if (!aiPrompt) return alert("Lütfen tarif ile ilgili bir tanım girin.");
        setIsGenerating(true);

        try {
            const response = await fetch('/api/generate-recipe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: aiPrompt })
            });

            const data = await response.json();
            if (data.error) throw new Error(data.error);

            const recipe = data.result;

            setFormData(prev => ({
                ...prev,
                title: recipe.title || prev.title,
                description: recipe.description || prev.description,
                instructions: Array.isArray(recipe.instructions) ? recipe.instructions : prev.instructions,
                time: recipe.time || prev.time,
                servings: recipe.servings || prev.servings,
                category: recipe.category || prev.category
            }));

            if (recipe.ingredients) {
                const lines = Array.isArray(recipe.ingredients)
                    ? recipe.ingredients
                    : recipe.ingredients.split('\n').filter(Boolean);
                setIngredientSections([{ id: 'sec-ai', title: '', items: lines }]);
            }

            setShowAiModal(false);
            alert("✨ Tarif başarıyla aktarıldı!");
        } catch (err) {
            alert("AI Tarif Üretme Hatası: " + err.message);
        } finally {
            setIsGenerating(false);
        }
    };

    const categories = [
        "Yurt Tarifleri",
        "Sporcu Tarifleri",
        "Diyet Tarifleri",
        "Tatlılar",
        "İçecekler",
        "Pratik & Hızlı",
        "Geleneksel Lezzetler"
    ];

    return (
        <div className="modern-editor-root">
            <style>{`
                .modern-editor-root {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: #f8fafc;
                    color: #0f172a;
                    font-family: 'Inter', -apple-system, sans-serif;
                    overflow: hidden;
                }

                .editor-scroll-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 24px 20px 80px;
                    max-width: 720px;
                    margin: 0 auto;
                    width: 100%;
                    box-sizing: border-box;
                }

                /* Top Navigation Bar */
                .editor-header-bar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    gap: 12px;
                }

                .editor-back-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.88rem;
                    font-weight: 700;
                    color: var(--primary, #6366f1);
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 6px 0;
                    transition: opacity 0.2s;
                }
                .editor-back-btn:hover { opacity: 0.8; }

                .editor-ai-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 24px;
                    font-size: 0.82rem;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
                    transition: all 0.2s ease;
                }
                .editor-ai-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 18px rgba(139, 92, 246, 0.4);
                }

                /* Tabs */
                .editor-tabs-pill {
                    display: flex;
                    background: #e2e8f0;
                    padding: 4px;
                    border-radius: 12px;
                    margin-bottom: 20px;
                    gap: 4px;
                }
                .editor-tab-item {
                    flex: 1;
                    padding: 8px 14px;
                    border: none;
                    background: transparent;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #64748b;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    transition: all 0.15s ease;
                }
                .editor-tab-item.active {
                    background: #ffffff;
                    color: var(--primary, #6366f1);
                    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
                }

                /* Banner & Media */
                .recipe-banner-wrap {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 16 / 9;
                    border-radius: 20px;
                    overflow: hidden;
                    background: #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
                    margin-bottom: 20px;
                }
                .recipe-banner-wrap img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .recipe-banner-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%);
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    padding: 16px;
                }
                .recipe-category-select {
                    background: rgba(15, 23, 42, 0.85);
                    backdrop-filter: blur(8px);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.2);
                    padding: 6px 14px;
                    border-radius: 30px;
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    cursor: pointer;
                    outline: none;
                }
                .recipe-media-change-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(8px);
                    color: #0f172a;
                    border: none;
                    padding: 8px 14px;
                    border-radius: 30px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                    transition: all 0.2s ease;
                }
                .recipe-media-change-btn:hover {
                    background: #ffffff;
                    transform: translateY(-1px);
                }

                .media-uploader-drawer {
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 16px;
                    margin-bottom: 20px;
                    animation: fadeIn 0.2s ease;
                }

                /* Title Input */
                .recipe-title-input {
                    width: 100%;
                    border: none;
                    background: transparent;
                    font-size: clamp(22px, 5vw, 32px);
                    font-weight: 800;
                    color: #0f172a;
                    outline: none;
                    padding: 6px 0;
                    margin-bottom: 16px;
                    font-family: inherit;
                    line-height: 1.2;
                }
                .recipe-title-input::placeholder {
                    color: #cbd5e1;
                }

                /* Meta Bar */
                .recipe-meta-bar {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                    margin-bottom: 18px;
                }
                .meta-pill-card {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 10px 14px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
                }
                .meta-pill-card .meta-icon {
                    color: var(--primary, #6366f1);
                    flex-shrink: 0;
                }
                .meta-pill-card input {
                    border: none;
                    background: transparent;
                    width: 100%;
                    font-size: 0.88rem;
                    font-weight: 600;
                    color: #1e293b;
                    outline: none;
                    font-family: inherit;
                }

                .video-link-card {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 10px 14px;
                    margin-bottom: 24px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
                }
                .video-link-card input {
                    border: none;
                    background: transparent;
                    width: 100%;
                    font-size: 0.88rem;
                    color: #1e293b;
                    outline: none;
                    font-family: inherit;
                }

                /* Modern Section Card */
                .editor-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 18px;
                    padding: 20px;
                    margin-bottom: 20px;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
                }
                .editor-card-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 14px;
                }
                .editor-card-title {
                    font-size: 1rem;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                /* Ingredients Builder */
                .ingredient-group-box {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 14px;
                    margin-bottom: 14px;
                }
                .ingredient-group-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    margin-bottom: 12px;
                }
                .ingredient-group-title-input {
                    font-size: 0.88rem;
                    font-weight: 700;
                    color: #1e293b;
                    background: transparent;
                    border: none;
                    border-bottom: 1.5px dashed #cbd5e1;
                    padding: 4px 2px;
                    width: 65%;
                    outline: none;
                }
                .ingredient-group-title-input:focus {
                    border-bottom-color: var(--primary, #6366f1);
                }
                .ingredient-group-actions {
                    display: flex;
                    gap: 6px;
                }
                .btn-text-action {
                    background: transparent;
                    border: none;
                    font-size: 0.76rem;
                    font-weight: 700;
                    cursor: pointer;
                    padding: 4px 8px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .btn-text-bulk { color: var(--primary, #6366f1); background: rgba(99, 102, 241, 0.08); }
                .btn-text-del { color: #ef4444; background: #fee2e2; }

                .ingredient-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 8px;
                }
                .ingredient-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: var(--primary, #6366f1);
                    flex-shrink: 0;
                }
                .ingredient-input {
                    flex: 1;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 8px 12px;
                    font-size: 0.88rem;
                    color: #0f172a;
                    font-family: inherit;
                    outline: none;
                    transition: border-color 0.15s;
                }
                .ingredient-input:focus {
                    border-color: var(--primary, #6366f1);
                }
                .btn-row-remove {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 6px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .btn-row-remove:hover {
                    color: #ef4444;
                    background: #fee2e2;
                }

                .btn-add-item-row {
                    width: 100%;
                    padding: 8px;
                    background: #ffffff;
                    border: 1px dashed #cbd5e1;
                    border-radius: 10px;
                    color: #475569;
                    font-size: 0.82rem;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    margin-top: 6px;
                    transition: all 0.15s;
                }
                .btn-add-item-row:hover {
                    border-color: var(--primary, #6366f1);
                    color: var(--primary, #6366f1);
                    background: rgba(99, 102, 241, 0.04);
                }

                .btn-add-group {
                    width: 100%;
                    padding: 12px;
                    background: rgba(99, 102, 241, 0.06);
                    border: 1.5px dashed rgba(99, 102, 241, 0.4);
                    border-radius: 14px;
                    color: var(--primary, #6366f1);
                    font-size: 0.88rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.15s;
                }
                .btn-add-group:hover {
                    background: rgba(99, 102, 241, 0.12);
                }

                /* Step Builder */
                .step-card {
                    display: flex;
                    gap: 12px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 14px;
                    margin-bottom: 12px;
                }
                .step-number-badge {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: white;
                    font-size: 0.82rem;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-top: 4px;
                }
                .step-content-area {
                    flex: 1;
                }
                .step-actions-col {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .step-action-btn {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    color: #64748b;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s;
                }
                .step-action-btn:hover {
                    color: var(--primary, #6366f1);
                    border-color: var(--primary, #6366f1);
                }
                .step-action-btn.del:hover {
                    color: #ef4444;
                    border-color: #fca5a5;
                    background: #fef2f2;
                }

                .btn-add-step {
                    width: 100%;
                    padding: 12px;
                    background: #ffffff;
                    border: 1.5px dashed #cbd5e1;
                    border-radius: 14px;
                    color: #475569;
                    font-size: 0.88rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.15s;
                }
                .btn-add-step:hover {
                    border-color: var(--primary, #6366f1);
                    color: var(--primary, #6366f1);
                    background: rgba(99, 102, 241, 0.04);
                }

                /* Sticky Action Footer */
                .editor-sticky-footer {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: rgba(255, 255, 255, 0.98);
                    backdrop-filter: blur(12px);
                    border-top: 1px solid #e2e8f0;
                    padding: 14px 20px calc(14px + env(safe-area-inset-bottom, 12px));
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    z-index: 10002;
                }
                .footer-inner-wrap {
                    max-width: 720px;
                    width: 100%;
                    margin: 0 auto;
                    display: flex;
                    justify-content: space-between;
                    gap: 12px;
                }
                .editor-cancel-btn {
                    flex: 1;
                    max-width: 160px;
                    padding: 12px 18px;
                    border-radius: 12px;
                    background: #f1f5f9;
                    border: 1px solid #cbd5e1;
                    color: #475569;
                    font-size: 0.9rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                }
                .editor-save-btn {
                    flex: 2;
                    max-width: 280px;
                    padding: 12px 24px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    border: none;
                    color: white;
                    font-size: 0.92rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
                    transition: all 0.2s;
                }
                .editor-save-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
                }

                /* Bulk Import Modal */
                .bulk-import-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                    padding: 16px;
                }
                .bulk-import-modal {
                    background: #ffffff;
                    width: 100%;
                    max-width: 480px;
                    border-radius: 20px;
                    padding: 24px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }

                @media (max-width: 640px) {
                    .editor-scroll-body { padding: 14px 14px 90px; }
                    .recipe-meta-bar { grid-template-columns: 1fr; }
                    .footer-inner-wrap { width: 100%; }
                    .editor-cancel-btn { max-width: none; }
                    .editor-save-btn { max-width: none; }
                }
            `}</style>

            {/* AI Assistant Modal */}
            {showAiModal && (
                <div className="bulk-import-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAiModal(false); }}>
                    <div className="bulk-import-modal">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Sparkles size={20} color="#8b5cf6" /> AI Tarif Oluşturucu
                            </h3>
                            <button onClick={() => setShowAiModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: '#64748b' }}>
                            İstediğiniz yemeği veya elinizdeki malzemeleri yazın, yapay zeka malzemeleri ve adımları otomatik hazırlasın.
                        </p>
                        <AutoTextarea
                            minRows={3}
                            placeholder="Örn: Fırında kremalı patates graten tarifi, 4 kişilik, pratik..."
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            style={{ border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '12px', marginBottom: '18px', background: '#f8fafc' }}
                        />
                        <button 
                            className="editor-save-btn" 
                            style={{ width: '100%', maxWidth: 'none' }} 
                            onClick={handleAiGenerate} 
                            disabled={isGenerating}
                        >
                            <Sparkles size={16} /> {isGenerating ? 'Tarif Üretiliyor...' : '✨ Tarifi Oluştur ve Aktar'}
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Paste Ingredients Modal */}
            {bulkSectionId && (
                <div className="bulk-import-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setBulkSectionId(null); }}>
                    <div className="bulk-import-modal">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <ClipboardList size={20} color="#6366f1" /> Malzemeleri Hızlı Yapıştır
                            </h3>
                            <button onClick={() => setBulkSectionId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: '#64748b' }}>
                            Malzeme listenizi alt alta veya virgülle ayırarak buraya yapıştırın. Sistem tek tıkla düzenli satırlara ayıracaktır.
                        </p>
                        <AutoTextarea
                            minRows={4}
                            placeholder={"Örn:\n500 ml süt\n1 çubuk tarçın\n125 gr toz şeker\n4 yumurta sarısı"}
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                            style={{ border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '12px', marginBottom: '18px', background: '#f8fafc' }}
                        />
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button className="editor-cancel-btn" onClick={() => setBulkSectionId(null)} style={{ flex: 1 }}>İptal</button>
                            <button className="editor-save-btn" onClick={handleApplyBulkText} style={{ flex: 1.5 }}>
                                <Check size={16} /> Listeye Aktar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Scrollable Form Body */}
            <div className="editor-scroll-body">
                {/* Header Actions */}
                <div className="editor-header-bar">
                    <button type="button" className="editor-back-btn" onClick={onCancel}>
                        <ArrowLeft size={18} /> Tariflere Dön
                    </button>
                    <button type="button" className="editor-ai-btn" onClick={() => setShowAiModal(true)}>
                        <Sparkles size={16} /> AI ile Oluştur
                    </button>
                </div>

                {/* Tabs */}
                <div className="editor-tabs-pill">
                    <button 
                        type="button" 
                        className={`editor-tab-item ${activeTab === 'editor' ? 'active' : ''}`}
                        onClick={() => setActiveTab('editor')}
                    >
                        <ChefHat size={16} /> Düzenleyici
                    </button>
                    <button 
                        type="button" 
                        className={`editor-tab-item ${activeTab === 'comments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('comments')}
                    >
                        <MessageSquare size={16} /> Yorumlar ({comments.length})
                    </button>
                </div>

                {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 16px', borderRadius: '12px', marginBottom: '16px', fontSize: '0.88rem' }}>
                        {error}
                    </div>
                )}

                {activeTab === 'editor' ? (
                    <>
                        {/* 16:9 Banner Cover */}
                        <div className="recipe-banner-wrap">
                            <img 
                                src={formData.imageBase64 || "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200"} 
                                alt="Kapak Görseli" 
                            />
                            <div className="recipe-banner-overlay">
                                <select 
                                    name="category" 
                                    value={formData.category} 
                                    onChange={handleChange} 
                                    className="recipe-category-select"
                                >
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                                <button 
                                    type="button" 
                                    className="recipe-media-change-btn"
                                    onClick={() => setShowUploader(!showUploader)}
                                >
                                    <Camera size={15} /> {showUploader ? 'Kapat' : 'Görseli Değiştir'}
                                </button>
                            </div>
                        </div>

                        {/* Collapsible Image Uploader */}
                        {showUploader && (
                            <div className="media-uploader-drawer">
                                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '10px' }}>
                                    Kapak Görseli Yükleyin veya Bağlantı Girin:
                                </span>
                                <ImageUploader 
                                    currentUrl={formData.imageBase64} 
                                    onUploadComplete={(url) => {
                                        setFormData(prev => ({ ...prev, imageBase64: url }));
                                        setShowUploader(false);
                                    }} 
                                />
                            </div>
                        )}

                        {/* Title Input */}
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Tarif Başlığı..."
                            className="recipe-title-input"
                            required
                        />

                        {/* Meta Pills (Prep Time & Servings) */}
                        <div className="recipe-meta-bar">
                            <div className="meta-pill-card">
                                <Clock size={18} className="meta-icon" />
                                <div>
                                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', display: 'block', fontWeight: 700 }}>HAZIRLAMA SÜRESİ</span>
                                    <input 
                                        type="text" 
                                        name="time" 
                                        value={formData.time} 
                                        onChange={handleChange} 
                                        placeholder="Örn: 45 Dk" 
                                    />
                                </div>
                            </div>

                            <div className="meta-pill-card">
                                <Users size={18} className="meta-icon" />
                                <div>
                                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', display: 'block', fontWeight: 700 }}>PORSİYON</span>
                                    <input 
                                        type="text" 
                                        name="servings" 
                                        value={formData.servings} 
                                        onChange={handleChange} 
                                        placeholder="Örn: 4 Kişilik" 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Video URL */}
                        <div className="video-link-card">
                            <Youtube size={20} color="#ef4444" style={{ flexShrink: 0 }} />
                            <input
                                type="text"
                                name="videoUrl"
                                value={formData.videoUrl}
                                onChange={handleChange}
                                placeholder="Youtube Video veya Shorts Linki (Opsiyonel)..."
                            />
                        </div>

                        {/* Description / Summary */}
                        <div className="editor-card">
                            <div className="editor-card-header">
                                <h3 className="editor-card-title">
                                    <BookOpen size={18} color="#6366f1" /> Tarif Özeti
                                </h3>
                            </div>
                            <AutoTextarea
                                name="description"
                                minRows={2}
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Tarif hakkında kısa, iştah açıcı bir özet yazın..."
                                style={{
                                    border: '1.5px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '12px',
                                    background: '#f8fafc'
                                }}
                            />
                        </div>

                        {/* Ingredients Section */}
                        <div className="editor-card">
                            <div className="editor-card-header">
                                <h3 className="editor-card-title">
                                    🛒 Malzemeler
                                </h3>
                            </div>

                            {ingredientSections.map((section, secIdx) => (
                                <div key={section.id} className="ingredient-group-box">
                                    <div className="ingredient-group-top">
                                        <input
                                            type="text"
                                            className="ingredient-group-title-input"
                                            placeholder={secIdx === 0 ? "Grup Başlığı (Opsiyonel, örn: Kek Hamuru)" : "Grup Başlığı (Örn: Krema İçin)"}
                                            value={section.title}
                                            onChange={(e) => handleSectionTitleChange(section.id, e.target.value)}
                                        />
                                        <div className="ingredient-group-actions">
                                            <button 
                                                type="button" 
                                                className="btn-text-action btn-text-bulk" 
                                                onClick={() => { setBulkSectionId(section.id); setBulkText(''); }}
                                                title="Hızlı toplu yapıştır"
                                            >
                                                <ClipboardList size={13} /> Toplu Yapıştır
                                            </button>
                                            {ingredientSections.length > 1 && (
                                                <button 
                                                    type="button" 
                                                    className="btn-text-action btn-text-del" 
                                                    onClick={() => removeSection(section.id)}
                                                    title="Grubu Sil"
                                                >
                                                    <Trash2 size={13} /> Sil
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Individual Ingredient Rows */}
                                    <div className="ingredient-rows-container">
                                        {section.items.map((item, itemIdx) => (
                                            <div key={itemIdx} className="ingredient-row">
                                                <div className="ingredient-dot"></div>
                                                <input
                                                    type="text"
                                                    className="ingredient-input"
                                                    placeholder="Örn: 500 ml süt veya 1 çimdik tuz"
                                                    value={item}
                                                    onChange={(e) => handleItemChange(section.id, itemIdx, e.target.value)}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn-row-remove"
                                                    onClick={() => removeItemRow(section.id, itemIdx)}
                                                    title="Malzemeyi Çıkar"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <button 
                                        type="button" 
                                        className="btn-add-item-row"
                                        onClick={() => addItemRow(section.id)}
                                    >
                                        <Plus size={14} /> Yeni Malzeme Satırı Ekle
                                    </button>
                                </div>
                            ))}

                            <button 
                                type="button" 
                                className="btn-add-group"
                                onClick={addSection}
                            >
                                <Plus size={16} /> Yeni Malzeme Grubu Ekle
                            </button>
                        </div>

                        {/* Steps / Instructions Section */}
                        <div className="editor-card">
                            <div className="editor-card-header">
                                <h3 className="editor-card-title">
                                    🔥 Hazırlanışı (Adım Adım)
                                </h3>
                            </div>

                            <div className="steps-container">
                                {formData.instructions.map((step, idx) => (
                                    <div key={idx} className="step-card">
                                        <div className="step-number-badge">{idx + 1}</div>
                                        <div className="step-content-area">
                                            <AutoTextarea
                                                minRows={2}
                                                placeholder={`Adım ${idx + 1} detaylarını yazın...`}
                                                value={step}
                                                onChange={(e) => handleStepChange(idx, e.target.value)}
                                                style={{
                                                    background: '#ffffff',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    padding: '10px 12px'
                                                }}
                                            />
                                        </div>
                                        <div className="step-actions-col">
                                            <button 
                                                type="button" 
                                                className="step-action-btn" 
                                                onClick={() => moveStep(idx, 'up')}
                                                disabled={idx === 0}
                                                title="Yukarı Taşı"
                                            >
                                                <ChevronUp size={15} />
                                            </button>
                                            <button 
                                                type="button" 
                                                className="step-action-btn" 
                                                onClick={() => moveStep(idx, 'down')}
                                                disabled={idx === formData.instructions.length - 1}
                                                title="Aşağı Taşı"
                                            >
                                                <ChevronDown size={15} />
                                            </button>
                                            <button 
                                                type="button" 
                                                className="step-action-btn del" 
                                                onClick={() => removeStep(idx)}
                                                title="Adımı Sil"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button 
                                type="button" 
                                className="btn-add-step"
                                onClick={addStep}
                            >
                                <Plus size={16} /> Yeni Adım Ekle
                            </button>
                        </div>
                    </>
                ) : (
                    /* Comments Management Tab */
                    <div className="editor-card" style={{ padding: '24px', textAlign: 'center' }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 800 }}>Tarif Yorumları</h4>
                        {commentsLoading ? (
                            <p style={{ color: '#94a3b8' }}>Yorumlar yükleniyor...</p>
                        ) : comments.length === 0 ? (
                            <p style={{ color: '#94a3b8' }}>Bu tarif için henüz bir yorum yapılmamış.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
                                {comments.map(c => (
                                    <div key={c.yorumId} style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <span style={{ fontWeight: 700, fontSize: '0.88rem', display: 'block' }}>{c.kullaniciAdi || 'Misafir'}</span>
                                            <span style={{ fontSize: '0.84rem', color: '#475569' }}>{c.yorum}</span>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={() => handleDeleteComment(c.yorumId)}
                                            style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer' }}
                                        >
                                            Sil
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Sticky Action Footer */}
            <div className="editor-sticky-footer">
                <div className="footer-inner-wrap">
                    <button type="button" className="editor-cancel-btn" onClick={onCancel} disabled={isSaving}>
                        <X size={16} /> Vazgeç
                    </button>
                    <button type="button" className="editor-save-btn" onClick={handleSubmit} disabled={isSaving}>
                        <Check size={18} /> {isSaving ? 'Kaydediliyor...' : 'Yayınla & Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecipeVisualEditor;
